import { Trash, NotePencil, ArrowsClockwise } from "@phosphor-icons/react";
import DeleteAppointmentDialog from "../../DeleteAppointmentDialog";
import {
  HEBREW_DAYS_FULL,
  formatHebrewDate,
} from "../../../../utils/dateUtils";

// "יום שלישי, 12/08/2025 בשעה 16:00"
const formatScopeWhen = (date, { withDate = true } = {}) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  const time = `${String(date.getHours()).padStart(2, "0")}:${String(
    date.getMinutes()
  ).padStart(2, "0")}`;
  const dayName = `יום ${HEBREW_DAYS_FULL[date.getDay()]}`;
  return withDate
    ? `${dayName}, ${formatHebrewDate(date)} בשעה ${time}`
    : `${dayName} בשעה ${time}`;
};

const TX_PAID_FAMILY = new Set(["paid", "invoice_manual", "invoice_auto"]);

/**
 * Edit-mode dialogs of the appointment wizard: delete (single + recurring
 * scope) and the series edit-scope dialog. Ported from AppointmentModal —
 * same options, same server args.
 */
function EditDialogs({ form }) {
  const {
    appointment,
    clinicId,
    showDeleteConfirm,
    setShowDeleteConfirm,
    deleteDialogWorking,
    isDeleting,
    isDeletingRecurring,
    handleDeleteConfirm,
    deleteScope,
    setDeleteScope,
    showRecurringDeleteDialog,
    setShowRecurringDeleteDialog,
    recurringDeleteState,
    setRecurringDeleteState,
    handleRecurringDeleteConfirm,
    showScopeDialog,
    editScope,
    setEditScope,
    confirmScopeAndSubmit,
    cancelScopeDialog,
    isUpdatingRecurring,
    scopeNewStart,
    laterShiftedMeetings,
    laterSeriesMeetings,
    postponableMeetings,
    canPostponeSeries,
    canPostponeAppointment,
    postponedNextMeetingDate,
    postponedLastMeetingDate,
    startPostpone,
    cancelFlowNode,
    isBlockMode,
  } = form;

  const recurringDeleteWorking =
    recurringDeleteState.isWorking || isDeletingRecurring;

  return (
    <>
      <DeleteAppointmentDialog
        isOpen={showDeleteConfirm && !!appointment}
        appointment={appointment}
        clinicId={clinicId}
        isWorking={deleteDialogWorking || isDeleting}
        onClose={() => {
          if (deleteDialogWorking) return;
          setShowDeleteConfirm(false);
        }}
        onConfirm={({ deleteLinkedTransaction }) =>
          handleDeleteConfirm(deleteLinkedTransaction)
        }
      />

      {/* Series edit-scope dialog */}
      {showScopeDialog && (
        <div className="apt-wiz-dialog-overlay" onClick={cancelScopeDialog}>
          <div className="apt-wiz-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="apt-wiz-dialog-header">
              <NotePencil size={22} weight="bold" />
              <h3>{isBlockMode ? "עריכת חסימה בסדרה" : "עריכת מפגש בסדרה"}</h3>
            </div>
            <p className="apt-wiz-dialog-message">
              {isBlockMode
                ? "חסימה זו היא חלק מסדרת חסימות. מה ברצונך לעדכן?"
                : "מפגש זה הוא חלק מסדרת מפגשים. מה ברצונך לעדכן?"}
            </p>
            <div className="apt-wiz-scope-options">
              <label className="apt-wiz-scope-option">
                <input
                  type="radio"
                  name="wizEditScope"
                  value="single"
                  checked={editScope === "single"}
                  onChange={(e) => setEditScope(e.target.value)}
                />
                <span className="apt-wiz-scope-label">
                  {isBlockMode ? "חסימה זו בלבד" : "מפגש זה בלבד"}
                  {scopeNewStart && (
                    <span className="apt-wiz-scope-hint">
                      רק מפגש זה יעבור ל{formatScopeWhen(scopeNewStart)} • שאר
                      המפגשים יישארו כפי שהם
                    </span>
                  )}
                </span>
              </label>
              <label className="apt-wiz-scope-option">
                <input
                  type="radio"
                  name="wizEditScope"
                  value="future"
                  checked={editScope === "future"}
                  onChange={(e) => setEditScope(e.target.value)}
                />
                <span className="apt-wiz-scope-label">
                  {isBlockMode
                    ? "חסימה זו וכל החסימות הבאות"
                    : "מפגש זה וכל המפגשים העתידיים"}
                  {scopeNewStart && (
                    <span className="apt-wiz-scope-hint">
                      כל המפגשים הבאים יעברו ל
                      {formatScopeWhen(scopeNewStart, { withDate: false })}
                      {laterShiftedMeetings.length > 0
                        ? ` • ${laterShiftedMeetings.length} מפגשים נוספים יזוזו`
                        : ""}
                    </span>
                  )}
                </span>
              </label>
              {isBlockMode && (
                <label className="apt-wiz-scope-option">
                  <input
                    type="radio"
                    name="wizEditScope"
                    value="all"
                    checked={editScope === "all"}
                    onChange={(e) => setEditScope(e.target.value)}
                  />
                  <span className="apt-wiz-scope-label">
                    כל החסימות בסדרה
                    <span className="apt-wiz-scope-hint">
                      הסיבה, ההערות והמשך יעודכנו בכל החסימות — כולל חסימות
                      שתאריכן כבר עבר
                    </span>
                  </span>
                </label>
              )}
              {canPostponeSeries && !isBlockMode && (
                <label className="apt-wiz-scope-option">
                  <input
                    type="radio"
                    name="wizEditScope"
                    value="postpone_week"
                    checked={editScope === "postpone_week"}
                    onChange={(e) => setEditScope(e.target.value)}
                  />
                  <span className="apt-wiz-scope-label">
                    מפגש זה בלבד, ולדחות את שאר המפגשים בשבוע
                    <span className="apt-wiz-scope-hint">
                      מפגש זה יעבור ל{formatScopeWhen(scopeNewStart)}
                      {postponedNextMeetingDate && (
                        <>
                          {" "}
                          • {postponableMeetings.length} המפגשים הבאים יישארו
                          באותו יום ושעה ויידחו בשבוע — הבא יתקיים ב
                          {formatScopeWhen(postponedNextMeetingDate)}
                        </>
                      )}
                      {postponedLastMeetingDate && (
                        <>
                          {" "}
                          • הסדרה תסתיים ב-{formatHebrewDate(postponedLastMeetingDate)}
                        </>
                      )}
                    </span>
                  </span>
                </label>
              )}
            </div>
            {editScope === "postpone_week" && (
              <p className="apt-wiz-dialog-note">
                שינויים בפרטים אחרים (מחיר, הערות) יחולו על מפגש זה בלבד — בשאר
                המפגשים ישתנה רק התאריך
              </p>
            )}
            <div className="apt-wiz-dialog-actions">
              <button
                type="button"
                className="apt-wiz-btn secondary"
                onClick={cancelScopeDialog}
              >
                ביטול
              </button>
              <button
                type="button"
                className="apt-wiz-btn primary"
                onClick={confirmScopeAndSubmit}
                disabled={isUpdatingRecurring}
              >
                {isUpdatingRecurring ? "מעדכן..." : "עדכן"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recurring delete dialog */}
      {showRecurringDeleteDialog &&
        (() => {
          const tx = recurringDeleteState.linkedTransaction;
          const isLoadingTx = tx === undefined;
          const hasTx = !!tx;
          const txIsPaidFamily = hasTx && TX_PAID_FAMILY.has(tx.status);
          const showIncludePastToggle = deleteScope !== "single";
          return (
            <div
              className="apt-wiz-dialog-overlay"
              onClick={() => {
                if (!recurringDeleteWorking) setShowRecurringDeleteDialog(false);
              }}
            >
              <div
                className="apt-wiz-dialog"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="apt-wiz-dialog-header danger">
                  <Trash size={22} weight="bold" />
                  <h3>מחיקת מפגש מסדרה</h3>
                </div>
                <p className="apt-wiz-dialog-message">
                  מפגש זה הוא חלק מסדרה של {appointment?.recurringTotalCount}{" "}
                  מפגשים. מה ברצונך למחוק?
                </p>
                <div className="apt-wiz-scope-options">
                  <label className="apt-wiz-scope-option">
                    <input
                      type="radio"
                      name="wizDeleteScope"
                      value="single"
                      checked={deleteScope === "single"}
                      onChange={(e) => setDeleteScope(e.target.value)}
                      disabled={recurringDeleteWorking}
                    />
                    <span className="apt-wiz-scope-label">
                      מפגש זה בלבד
                      <span className="apt-wiz-scope-hint">
                        המפגש יוסר מהסדרה לחלוטין ולא ייווצר מפגש חלופי במקומו.
                        שאר המפגשים יישארו במועדים שלהם.
                      </span>
                    </span>
                  </label>
                  <label className="apt-wiz-scope-option">
                    <input
                      type="radio"
                      name="wizDeleteScope"
                      value="future"
                      checked={deleteScope === "future"}
                      onChange={(e) => setDeleteScope(e.target.value)}
                      disabled={recurringDeleteWorking}
                    />
                    <span className="apt-wiz-scope-label">
                      מפגש זה וכל המפגשים העתידיים
                      {laterSeriesMeetings.length > 0 && (
                        <span className="apt-wiz-scope-hint">
                          {`יימחקו ${laterSeriesMeetings.length + 1} מפגשים (כולל מפגש זה). מפגשים שכבר התקיימו יישארו, אלא אם תסמן את האפשרות למטה.`}
                        </span>
                      )}
                    </span>
                  </label>
                  <label className="apt-wiz-scope-option">
                    <input
                      type="radio"
                      name="wizDeleteScope"
                      value="all"
                      checked={deleteScope === "all"}
                      onChange={(e) => setDeleteScope(e.target.value)}
                      disabled={recurringDeleteWorking}
                    />
                    <span className="apt-wiz-scope-label">
                      כל המפגשים בסדרה
                      <span className="apt-wiz-scope-hint">
                        הסדרה כולה תימחק. מפגשים שכבר התקיימו יישארו, אלא אם
                        תסמן את האפשרות למטה.
                      </span>
                    </span>
                  </label>
                </div>

                {deleteScope === "single" && canPostponeAppointment && !isBlockMode && (
                  <button
                    type="button"
                    className="apt-wiz-postpone-alt"
                    onClick={() => {
                      setShowRecurringDeleteDialog(false);
                      startPostpone(appointment);
                    }}
                  >
                    <ArrowsClockwise size={16} weight="duotone" />
                    <span>
                      <strong>לא רוצה לאבד את המפגש?</strong> אפשר לדחות אותו
                      בשבוע במקום למחוק
                    </span>
                  </button>
                )}

                {showIncludePastToggle && (
                  <label className="apt-wiz-dialog-checkbox">
                    <input
                      type="checkbox"
                      checked={recurringDeleteState.includePast}
                      onChange={(e) =>
                        setRecurringDeleteState((prev) => ({
                          ...prev,
                          includePast: e.target.checked,
                        }))
                      }
                      disabled={recurringDeleteWorking}
                    />
                    <span>למחוק גם מפגשים שכבר התקיימו</span>
                  </label>
                )}

                {isLoadingTx ? (
                  <p className="apt-wiz-dialog-note">בודק תשלום מקושר…</p>
                ) : hasTx ? (
                  <label className="apt-wiz-dialog-checkbox">
                    <input
                      type="checkbox"
                      checked={recurringDeleteState.deleteLinkedTransactions}
                      onChange={(e) =>
                        setRecurringDeleteState((prev) => ({
                          ...prev,
                          deleteLinkedTransactions: e.target.checked,
                        }))
                      }
                      disabled={recurringDeleteWorking}
                    />
                    <span>
                      למחוק גם את התשלום המקושר למפגש זה
                      {txIsPaidFamily ? " (שימו לב: התשלום כבר שולם)" : ""}
                    </span>
                  </label>
                ) : recurringDeleteState.preloadError ? (
                  <p className="apt-wiz-dialog-note">
                    {recurringDeleteState.preloadError}
                  </p>
                ) : null}

                <div className="apt-wiz-dialog-actions">
                  <button
                    type="button"
                    className="apt-wiz-btn secondary"
                    onClick={() => setShowRecurringDeleteDialog(false)}
                    disabled={recurringDeleteWorking}
                  >
                    ביטול
                  </button>
                  <button
                    type="button"
                    className="apt-wiz-btn danger"
                    onClick={handleRecurringDeleteConfirm}
                    disabled={recurringDeleteWorking}
                  >
                    {recurringDeleteWorking ? "מוחק..." : "מחיקה"}
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

      {/* Shared cancel/postpone flow (its own modals) */}
      {cancelFlowNode}
    </>
  );
}

export default EditDialogs;
